import {useCallback, useState} from "react";

export interface LoadingState {
  isLoading: boolean;
  error: string | null;
  hasError: boolean;
  isSuccess: boolean;
}

export interface UseLoadingStateReturn extends LoadingState {
  startLoading: () => void;
  stopLoading: () => void;
  setError: (error: string) => void;
  clearError: () => void;
  reset: () => void;
  executeWithLoading: <T>(asyncFunction: () => Promise<T>) => Promise<T>;
}

export const useLoadingState = (): UseLoadingStateReturn => {
  const [state, setState] = useState<LoadingState>({
    isLoading: false,
    error: null,
    hasError: false,
    isSuccess: false,
  });

  const startLoading = useCallback(() => {
    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
      hasError: false,
      isSuccess: false,
    }));
  }, []);

  const stopLoading = useCallback(() => {
    setState(prev => ({
      ...prev,
      isLoading: false,
      isSuccess: false,
    }));
  }, []);

  const setError = useCallback((error: string) => {
    setState(prev => ({
      ...prev,
      isLoading: false,
      error,
      hasError: true,
      isSuccess: false,
    }));
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({
      ...prev,
      error: null,
      hasError: false,
      isSuccess: false,
    }));
  }, []);

  const reset = useCallback(() => {
    setState({
      isLoading: false,
      error: null,
      hasError: false,
      isSuccess: false,
    });
  }, []);
  const setSuccess = useCallback(() => {
    setState(prev => ({
      ...prev,
      isSuccess: true,
    }));
  }, []);

  const executeWithLoading = useCallback(
    async <T>(asyncFunction: () => Promise<T>): Promise<T> => {
      try {
        startLoading();
        const result = await asyncFunction();
        stopLoading();
        setSuccess();
        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? `Une erreur s'est produite: ${error.message}`
            : "Une erreur inattendue s'est produite";
        setError(errorMessage);
        throw error;
      }
    },
    [startLoading, stopLoading, setError]
  );

  return {
    ...state,
    startLoading,
    stopLoading,
    setError,
    clearError,
    reset,
    executeWithLoading,
  };
};
